import { createContext, useContext } from 'react';
import type { ExperimentAnalysis, Iteration } from '@gonfalon/experiments/types';
import type { UseQueryResult } from '@tanstack/react-query';

import { mustGetPrimaryMetricKey } from '#components/experimentation/common/utils/experiment';

type AnalysisQueryParams = {
  metricKey: string;
} & (
  | {
      attributeName: string;
      attributeValue: string | number | boolean;
    }
  | {
      attributeName?: undefined;
      attributeValue?: undefined;
    }
);

type AnalysisQuery = AnalysisQueryParams & {
  query: UseQueryResult<ExperimentAnalysis>;
};

const AnalysisQueriesContext = createContext<AnalysisQuery[] | undefined>(undefined);

export const useHasAnalysisContext = () => useContext(AnalysisQueriesContext) !== undefined;

export function IterationAnalysisProvider(props: { value: AnalysisQuery[]; children: React.ReactNode }) {
  return <AnalysisQueriesContext.Provider {...props} />;
}

/**
 * Returns the primary metric's analysisTime and pending state when IterationAnalysisProvider
 * is mounted. Safe outside the provider (settled with no time) so callers can fall back
 * without throwing.
 */
export function usePrimaryMetricAnalysisTime(
  iteration: Pick<Iteration, 'primarySingleMetric' | 'primaryFunnel'> | null | undefined,
): { analysisTime: number | undefined; isPending: boolean } {
  const ctx = useContext(AnalysisQueriesContext);
  if (!ctx) {
    return { analysisTime: undefined, isPending: false };
  }
  const metricKey = mustGetPrimaryMetricKey(iteration);
  if (!metricKey) {
    return { analysisTime: undefined, isPending: false };
  }
  // Prefer the unsliced analysis for this metric (attribute slices are listed after it).
  const analysisQuery =
    ctx.find((query) => query.metricKey === metricKey && query.attributeName === undefined) ??
    ctx.find((query) => query.metricKey === metricKey);
  if (!analysisQuery) {
    return { analysisTime: undefined, isPending: false };
  }
  return {
    analysisTime: analysisQuery.query.data?.analysisTime ?? undefined,
    isPending: analysisQuery.query.isPending,
  };
}

export function useIterationAnalysisQueries({ metricKey }: { metricKey?: string }): AnalysisQuery[] {
  const ctx = useContext(AnalysisQueriesContext);

  if (!ctx) {
    throw new Error('useIterationAnalysisQueries must be used within an IterationAnalysisProvider');
  }

  if (metricKey) {
    return ctx.filter((query) => query.metricKey === metricKey);
  }

  return ctx;
}

// returns the query for a given metric (consumer must handle loading/error states)
export function useIterationAnalysis({
  metricKey,
  attributeName,
  attributeValue,
}: AnalysisQueryParams): UseQueryResult<ExperimentAnalysis> {
  const ctx = useContext(AnalysisQueriesContext);

  if (!ctx) {
    throw new Error('useIterationAnalysis must be used within an IterationAnalysisProvider');
  }

  const analysisQuery = ctx.find(
    (query) =>
      query.metricKey === metricKey && query.attributeName === attributeName && query.attributeValue === attributeValue,
  );

  if (!analysisQuery) {
    throw new Error(
      `No analysis query found for ${metricKey}${attributeName ? `:${attributeName}:${attributeValue}` : ''}`,
    );
  }

  return analysisQuery.query;
}

// merges into one query for all metrics, with a shared loading/error state
export function useIterationAnalyses():
  | { isPending: true; isError: false; data: undefined }
  | { isPending: false; isError: true; data: undefined }
  | { isPending: false; isError: false; data: ExperimentAnalysis[] } {
  const ctx = useContext(AnalysisQueriesContext);

  if (!ctx) {
    throw new Error('useIterationAnalysis must be used within an IterationAnalysisProvider');
  }

  if (ctx.some(({ query }) => query.isPending)) {
    return { isPending: true, isError: false, data: undefined };
  }
  if (ctx.some(({ query }) => query.isError)) {
    return { isPending: false, isError: true, data: undefined };
  }
  const data = ctx.reduce<ExperimentAnalysis[]>((acc, { query }) => {
    if (!query.data) {
      // not expected to happen given isPending/isError above
      throw new Error('Premature query mapping');
    }
    acc.push(query.data);
    return acc;
  }, []);
  return { isPending: false, isError: false, data };
}

export function useIterationAnalysesMetricCounts() {
  const { isPending, isError, data } = useIterationAnalyses();

  if (isError) {
    return { isPending, isError, totalMetricsCount: 0, adjustedMetricsCount: 0, percentileMetricsCount: 0 };
  }

  const [allMetrics, percentileMetrics, adjustedMetrics] =
    data?.reduce(
      (acc, { metric, usingCUPED }) => {
        if (!metric) {
          return acc;
        }
        acc[0].add(metric.key);
        if (metric.analysisType === 'percentile') {
          acc[1].add(metric.key);
        }
        if (usingCUPED) {
          acc[2].add(metric.key);
        }
        return acc;
      },
      // using sets to de-duplicate
      [new Set<string>(), new Set<string>(), new Set<string>()],
    ) ?? [];

  return {
    isPending,
    isError,
    totalMetricsCount: allMetrics?.size,
    adjustedMetricsCount: adjustedMetrics?.size,
    percentileMetricsCount: percentileMetrics?.size,
  };
}
